import { Injectable, EventEmitter, Output } from '@angular/core';
import { LanguageService } from './../core/language.service';
import { DateRange } from 'app/shared/services/date-service';
import { InputSearchCriteria } from 'app/dashboard/leads/visits-request';
import { Segment, QueryContext, DatePeriodType } from './query-context';
import { SelectionType, SEARCHKEYS } from './segments';
import { CriteriaOperator } from 'app/core/operator';

@Injectable()
export class SearchService {
  @Output() public loadDefaultSegment = new EventEmitter<SelectionType>();
  private queryContext = new QueryContext();
  private pageView: SelectionType;

  constructor(private readonly languageService: LanguageService) {
    this.queryContext.segment = new Segment();
  }

  public addDefaultSettings() {
    if (this.pageView === SelectionType.Visits) {
      this.queryContext.take = 50; // TODO: Take as parameter
    } else {
      this.queryContext.take = 70; // TODO: Take as parameter
    }

    this.queryContext.dateRange = new DateRange();
    this.queryContext.segment = new Segment();
    this.queryContext.segment.criterias = {};

    // We only want to list identified profiles
    this.queryContext.segment.criterias['HasVisitor'] = {
      operator: CriteriaOperator.Equals,
      value: true,
    };
  }

  public fillSegmentQueryContext(segment: Segment) {
    const oldSortOrder = this.queryContext.orderBy;
    this.queryContext = new QueryContext();
    this.addDefaultSettings();
    const previousCriteria = JSON.parse(JSON.stringify(this.queryContext.segment.criterias || {}));
    this.queryContext.orderBy = oldSortOrder;
    this.queryContext.segment = new Segment();
    this.queryContext.segment.id = segment.id;
    this.queryContext.segment.name = segment.name;
    const newCriteria = JSON.parse(JSON.stringify(segment.criterias || {}));
    if (this.pageView === SelectionType.People) {
      this.queryContext.segment.criterias = Object.assign(previousCriteria, newCriteria);
    } else {
      this.queryContext.segment.criterias = newCriteria; // avoid copying reference
    }
    return this.queryContext;
  }

  public clearQueryContext() {
    this.queryContext = new QueryContext();
    this.addDefaultSettings();
  }

  public criteriaPropertyKey(type: string) {
    return SEARCHKEYS.filter((k) => k.key === type)[0];
  }

  public fillQueryContext(inputSearchCriteria: InputSearchCriteria) {
    if (!this.queryContext.segment.criterias) {
      this.queryContext.segment.criterias = {};
    }

    const key = this.criteriaPropertyKey(inputSearchCriteria.type);

    if (
      inputSearchCriteria.type === 'goals' ||
      inputSearchCriteria.type === 'campaigns' ||
      inputSearchCriteria.type === 'notGoals' ||
      inputSearchCriteria.type === 'notCampaigns' ||
      inputSearchCriteria.type === 'profilings' ||
      inputSearchCriteria.type === 'visitedPages' ||
      inputSearchCriteria.type === 'labels' ||
      inputSearchCriteria.type === 'country' ||
      inputSearchCriteria.type === 'accepted' ||
      inputSearchCriteria.type === 'referrerURL' ||
      inputSearchCriteria.type.startsWith('widget-')
    ) {
      this.queryContext.segment.criterias[key.value] = {
        operator: inputSearchCriteria.operator,
        value: this.convertToArray(inputSearchCriteria.input),
      };
    } else if (inputSearchCriteria.type === 'consent') {
      if (inputSearchCriteria.input == 'any') {
        this.queryContext.segment.criterias[key.value] = {
          operator: <any>(inputSearchCriteria.operator === 0 ? 'Any' : 'NotAny'), // Use real operators instead of equals/not-equals with value "any" like done in legacy
          value: null,
        };
      } else {
        this.queryContext.segment.criterias[key.value] = {
          operator: inputSearchCriteria.operator,
          value: this.convertToArray(inputSearchCriteria.input),
        };
      }
    } else if (
      inputSearchCriteria.type === 'utmMedium' ||
      inputSearchCriteria.type === 'utmSource' ||
      inputSearchCriteria.type === 'browserLang' ||
      inputSearchCriteria.type === 'device' ||
      inputSearchCriteria.type === 'siteTime' ||
      inputSearchCriteria.type === 'totalVisits' ||
      inputSearchCriteria.type === 'pageViews' ||
      inputSearchCriteria.type === 'TextSearch' ||
      inputSearchCriteria.type === 'totalVisitsPeople'
    ) {
      this.queryContext.segment.criterias[key.value] = {
        operator: inputSearchCriteria.operator,
        value: inputSearchCriteria.input,
      };
    } else if (inputSearchCriteria.type === 'facebook' || inputSearchCriteria.type === 'linkedIn') {
      this.addMultipleValuesinReferrer('referrer_domain', inputSearchCriteria.type.toLowerCase(), 2);
    } else if (
      inputSearchCriteria.type === 'socialMedia' ||
      inputSearchCriteria.type === 'paidTraffic' ||
      inputSearchCriteria.type === 'emailCampaigns'
    ) {
      this.addMultipleValuesinReferrer('referrer_type', key.value, 0); // referrer type is Equal to the word
    } else if (inputSearchCriteria.type === 'identified') {
      const operator = inputSearchCriteria.input ? CriteriaOperator.Equals : CriteriaOperator.NotEquals;
      this.queryContext.segment.criterias['HasVisitor'] = {
        operator: operator,
        value: true,
      };
    } else if (inputSearchCriteria.type == 'nps-score') {
      this.queryContext.segment.criterias['NpsScores'] = {
        operator: inputSearchCriteria.operator,
        value: inputSearchCriteria.input,
      };
    } else if (inputSearchCriteria.type == 'csat-score') {
      this.queryContext.segment.criterias['CsatScores'] = {
        operator: inputSearchCriteria.operator,
        value: inputSearchCriteria.input,
      };
    }
  }

  public async addMultipleValuesinReferrer(type: string, value: any, operator: number) {
    if (this.queryContext.segment.criterias[type]) {
      const multiValue = this.queryContext.segment.criterias[type].value;

      if (Array.isArray(multiValue)) {
        multiValue.push(value);
      }
      this.queryContext.segment.criterias[type].value = multiValue;
    } else {
      this.queryContext.segment.criterias[type] = {
        operator: operator,
        value: [value],
      }; // referrer domain CONTAINS the word
    }
  }

  public async removeMultipleValuesinReferrer(type: string, value: any) {
    if (this.queryContext.segment.criterias[type]) {
      const multiValue = this.queryContext.segment.criterias[type].value;

      if (Array.isArray(multiValue)) {
        const index = multiValue.indexOf(value);

        if (index >= 0) {
          multiValue.splice(index, 1);
        }
      }

      if (multiValue.length === 0) {
        delete this.queryContext.segment.criterias[type]; // delete entire object when all chips values are removed
      } else {
        this.queryContext.segment.criterias[type].value = multiValue;
      }
    }
  }

  public async addDateRange(startDate: Date, endDate: Date, datePeriod: DatePeriodType) {
    if (startDate) {
      (this.queryContext.dateRange as DateRange).startDate = startDate.toDateString();
    }
    if (endDate || (datePeriod !== undefined && endDate === undefined)) {
      (this.queryContext.dateRange as DateRange).endDate = endDate?.toDateString() || undefined;
    }

    this.queryContext.segment.datePeriod = datePeriod;
  }

  public getQueryContext(): QueryContext {
    if (this.queryContext.segment.criterias && 'consentversions' in this.queryContext.segment.criterias) {
      Object.defineProperty(
        this.queryContext.segment.criterias,
        'consentVersions',
        Object.getOwnPropertyDescriptor(this.queryContext.segment.criterias, 'consentversions'),
      );
      delete this.queryContext.segment.criterias['consentversions'];
    }
    return this.queryContext;
  }

  public async deleteFromQueryContext(type: string) {
    const key = this.criteriaPropertyKey(type);
    if (
      type === 'goals' ||
      type === 'campaigns' ||
      type === 'profilings' ||
      type === 'visitedPages' ||
      type === 'labels' ||
      type === 'country' ||
      type === 'utmMedium' ||
      type === 'utmSource' ||
      type === 'referrerURL' ||
      type === 'browserLang' ||
      type === 'device' ||
      type === 'siteTime' ||
      type === 'totalVisits' ||
      type === 'pageViews' ||
      type === 'identified' ||
      type === 'TextSearch' ||
      type === 'totalVisitsPeople' ||
      type === 'consented' ||
      type === 'accepted' ||
      type === 'consent' ||
      type === 'nps-score' ||
      type === 'csat-score' ||
      type.startsWith('widget-')
    ) {
      delete this.queryContext.segment.criterias[key.value];
    } else if (type === 'facebook' || type === 'linkedIn' || type === 'referrer_domain') {
      this.removeMultipleValuesinReferrer('referrer_domain', type);
    } else {
      if (type === 'socialMedia' || type === 'paidTraffic' || type === 'emailCampaigns' || type === 'referrer_type') {
        this.removeMultipleValuesinReferrer('referrer_type', key.value);
      }
    }
  }

  public getCriteriaValue(key: string) {
    if (this.queryContext.segment.criterias.hasOwnProperty(key)) {
      return this.queryContext.segment.criterias[key];
    } else {
      return null;
    }
  }

  private convertToArray(input: any) {
    let val;
    if (!Array.isArray(input)) {
      // convert single string to array
      val = [input];
    } else {
      val = input;
    }

    return val;
  }

  public setPageView(page: SelectionType) {
    this.pageView = page;
  }

  public getPageView() {
    return this.pageView;
  }

  public async updateOrderby(orderby: string) {
    this.queryContext.orderBy = orderby;
    this.queryContext.skip = 0; // reset page number when sorting changes
  }

  public async updatePageNumber(pagenumber: number, pageSize: number) {
    this.queryContext.skip = pagenumber * pageSize; // get previous page
  }

  public async formatsubListHeader(
    displayedEntities: number,
    totalCount: number,
    typeTranslationKey: string,
    pageSize: number,
  ): Promise<string> {
    // TODO: 50 hardcoded or in-param?
    const type = <string>(await this.languageService.translateAsync(typeTranslationKey)).toLowerCase();

    if (totalCount == null || totalCount === -1) {
      // if cosmos didn't count results (-1) - don't display text
      return '';
    } else if (totalCount === 0) {
      return await this.languageService.translateAsync('search.results.none');
    }

    let text: string;
    if (totalCount > pageSize) {
      text = await this.languageService.translateAsync('search.results.more', {
        type: type,
        totalCount: totalCount.toLocaleString(),
        displayedEntities: displayedEntities,
      });
    } else {
      text = await this.languageService.translateAsync('search.results.allShown', {
        type: type,
        totalCount: totalCount,
      });
    }
    return text;
  }
}
